// PRD §7.6 — comm-gated mentor profile for founders.
// Only reachable for mentors the founder shares a thread with (engagement
// or accepted mentor proposal). Backend enforces this — UI just renders.
import { useEffect, useState } from "react";
import { useRoute, Link } from "wouter";
import { ArrowLeft, MapPin, Linkedin, Tags, BookOpen } from "lucide-react";
import ProgressLoader from "@/components/progress-loader";
import { fp, useCohort } from "@/lib/founder-cohort";

const BASE = import.meta.env.BASE_URL;
const auth = (): Record<string, string> => ({ "x-founder-token": localStorage.getItem("founder_token") || "" });

interface MentorProfile {
  id: number;
  fullName: string;
  headline: string | null;
  bio: string | null;
  location: string | null;
  linkedinUrl: string | null;
  expertiseTags: string[];
  depth: { tag: string; description: string }[];
}

function safeHttpUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    if (u.protocol === "http:" || u.protocol === "https:") return u.toString();
  } catch { /* ignore */ }
  return null;
}

export default function FounderMentorDetail() {
  const cohort = useCohort();
  const [, params] = useRoute(`/${cohort.slug}/founder/mentors/:id`);
  const id = Number(params?.id);
  const [mentor, setMentor] = useState<MentorProfile | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<number | null>(null);

  useEffect(() => {
    if (!Number.isFinite(id)) return;
    fetch(`${BASE}api/founder-portal/mentors/${id}`, { headers: auth() })
      .then(async (r) => {
        setStatus(r.status);
        if (r.ok) return r.json();
        const d = await r.json().catch(() => ({}));
        return Promise.reject(d.error || `${r.status}`);
      })
      .then((d) => setMentor(d.mentor))
      .catch((e) => setErr(String(e)));
  }, [id]);

  const back = (
    <Link href={fp(cohort.slug, "mentors")} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1 mb-4">
      <ArrowLeft className="w-3 h-3" /> Mentor Connect
    </Link>
  );

  if (err) {
    const isGate = status === 403;
    return (
      <div className="max-w-3xl mx-auto px-6 py-10">
        {back}
        <div
          className={`rounded-md border px-4 py-4 text-sm ${isGate ? "border-amber-500/30 bg-amber-500/10 text-amber-100" : "border-red-500/30 bg-red-500/10 text-red-200"}`}
          data-testid={isGate ? "gate-403" : "error-mentor-detail"}
        >
          {err}
        </div>
      </div>
    );
  }
  if (!mentor) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10">
        {back}
        <ProgressLoader active={true} title="Loading" stages={["Loading mentor profile"]} />
      </div>
    );
  }

  const safeLinkedin = safeHttpUrl(mentor.linkedinUrl);

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
      {back}
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6" data-testid="mentor-header">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-mentor-name">{mentor.fullName}</h1>
        {mentor.headline && (
          <div className="text-base text-white/80 mt-1.5" data-testid="text-mentor-headline">{mentor.headline}</div>
        )}
        <div className="flex flex-wrap gap-3 mt-3 text-xs text-white/60">
          {mentor.location && (
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {mentor.location}</span>
          )}
          {safeLinkedin && (
            <a href={safeLinkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white underline" data-testid="link-mentor-linkedin">
              <Linkedin className="w-3 h-3" /> LinkedIn
            </a>
          )}
        </div>
      </div>

      {mentor.bio && (
        <section className="bg-zinc-900/40 border border-white/10 rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mb-2 inline-flex items-center gap-1"><BookOpen className="w-3 h-3" /> Bio</div>
          <p className="text-sm text-white/85 leading-relaxed whitespace-pre-line" data-testid="text-mentor-bio">{mentor.bio}</p>
        </section>
      )}

      {mentor.expertiseTags.length > 0 && (
        <section className="bg-zinc-900/40 border border-white/10 rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mb-2 inline-flex items-center gap-1"><Tags className="w-3 h-3" /> Expertise</div>
          <div className="flex flex-wrap gap-1.5">
            {mentor.expertiseTags.map((t) => (
              <span key={t} className="text-[11px] px-2 py-0.5 rounded bg-white/[0.06] text-white/80" data-testid={`tag-${t}`}>{t}</span>
            ))}
          </div>
        </section>
      )}

      {mentor.depth.length > 0 && (
        <section className="bg-zinc-900/40 border border-white/10 rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mb-3">What they've actually done</div>
          <div className="space-y-3">
            {mentor.depth.map((d, i) => (
              <div key={i} className="text-sm" data-testid={`depth-${d.tag}`}>
                <div className="text-white/60 text-[11px] uppercase tracking-wider font-mono mb-0.5">{d.tag}</div>
                <div className="text-white/85">{d.description}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
